// lib/blog/shared/blog-utils.tsx - Extract shared utilities to reduce duplication

import { formatForDisplay } from '~/lib/blog/utils/formatting'
import { logClientError } from '~/lib/logger-client'

export interface Breadcrumb {
  label: string
  href: string
  current?: boolean
  type: 'category' | 'subcategory' | 'article'
}

/**
 * Error details interface for comprehensive logging
 * 
 * @interface ErrorDetails
 * @property {string} message - The primary error message
 * @property {string} [stack] - The complete stack trace
 * @property {string} name - The JavaScript error type/name
 * @property {string} timestamp - ISO timestamp of when the error occurred
 * @property {string} [context] - Additional context about where the error occurred
 * @property {string} [pathname] - The pathname where the error occurred
 * @property {string} [errorId] - Unique identifier for tracking this specific error instance
 * @property {'low' | 'medium' | 'high' | 'critical'} [severity] - Severity level for error prioritization
 * @property {Record<string, unknown>} [metadata] - Additional metadata for debugging
 */
export interface ErrorDetails {
  message: string
  stack?: string
  name: string
  timestamp: string
  context?: string
  pathname?: string
  errorId?: string
  severity?: 'low' | 'medium' | 'high' | 'critical'
  metadata?: Record<string, unknown>
}

/**
 * Log levels for structured logging
 */
export type LogLevel = 'info' | 'error' | 'debug' | 'warn'

/**
 * Severity levels for error classification
 */
export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical'

// Development-only logging flag for compile-time optimization
const IS_DEV = process.env.NODE_ENV === 'development'

/**
 * Enhanced error logging with structured data and development gating
 * 
 * This function provides comprehensive error logging that serves multiple purposes:
 * 1. **Development Debugging**: Rich, grouped console output with full context
 * 2. **Production Monitoring**: Structured data for external monitoring services (critical errors only)
 * 3. **Performance**: Lazy evaluation and conditional execution
 * 
 * @param {Error} error - The Error object that occurred
 * @param {string} [context] - Additional context about where the error occurred
 * @param {string} [pathname] - The pathname where the error occurred
 * @param {string} [errorId] - Optional unique identifier for tracking
 * @param {'low' | 'medium' | 'high' | 'critical'} [severity='medium'] - Severity level
 * @param {Record<string, unknown>} [metadata] - Additional metadata for debugging
 */
export function logBlogError(
  error: Error,
  context?: string,
  pathname?: string,
  errorId?: string,
  severity: ErrorSeverity = 'medium',
  metadata?: Record<string, unknown>
): void {
  // Early return for production non-critical errors to minimize performance impact
  if (!IS_DEV && severity !== 'critical') {
    return
  }

  // Input validation with early return
  if (!error || typeof error !== 'object' || !error.message) {
    if (IS_DEV) {
      console.error('logBlogError: Invalid error object provided')
    }
    return
  }

  // Lazy evaluation: only build error details when needed
  const buildErrorDetails = (): ErrorDetails => ({
    message: error.message,
    stack: error.stack,
    name: error.name,
    timestamp: new Date().toISOString(),
    context,
    pathname,
    errorId,
    severity,
    metadata
  })

  try {
    if (IS_DEV) {
      // Development environment: Rich, organized console output
      const errorDetails = buildErrorDetails()
      
      console.group('🚨 Blog Error')
      console.error('Error:', error)
      console.table(errorDetails)
      
      // Additional context information for debugging
      if (context) console.info('Context:', context)
      if (pathname) console.info('Pathname:', pathname)
      if (errorId) console.info('Error ID:', errorId)
      if (severity) console.info('Severity:', severity)
      if (metadata && Object.keys(metadata).length > 0) {
        console.info('Metadata:', metadata)
      }
      
      console.groupEnd()
    } else {
      // Production: route critical blog errors through the shared client drain
      // (→ /api/log → BetterStack). `logClientError` redacts the URL, filters
      // benign noise, and classifies the error; it no-ops server-side, which is
      // fine because this util is client-reachable. Only criticals reach here
      // (non-criticals returned early above).
      logClientError(error, {
        component: 'blog-utils',
        context,
        errorPathname: pathname,
        errorId,
        severity,
        service: 'blog-component',
      })
    }
  } catch (loggingError) {
    // Fallback logging if structured logging fails
    if (IS_DEV) {
      console.error('Console logging failed:', loggingError)
      console.error('Original error:', error.message)
    } else {
      // In production, only log critical errors even in fallback
      if (severity === 'critical') {
        console.error('Critical error logging failed:', error.message)
      }
    }
  }
}

/**
 * Enhanced logging function for blog operations.
 * Gates logging based on environment - only logs in development.
 * 
 * @param level - Log level ('info' | 'error' | 'debug' | 'warn')
 * @param message - Primary log message
 * @param data - Additional structured data to log
 */
export function logBlogOperation(
  level: LogLevel,
  message: string,
  data?: Record<string, unknown>
): void {
  // Only log in development environment - no production logging for operations
  if (!IS_DEV) {
    return
  }

  const timestamp = new Date().toISOString()
  const logData = {
    timestamp,
    component: 'BlogUtils',
    level,
    message,
    ...data
  } as const

  try {
    const emoji = {
      error: '🚨',
      debug: '🔍',
      info: 'ℹ️',
      warn: '⚠️'
    } as const

    const logMethod = {
      error: console.error,
      debug: console.debug,
      info: console.info,
      warn: console.warn
    } as const

    console.group(`${emoji[level]} Blog Operation ${level.charAt(0).toUpperCase() + level.slice(1)}`)
    logMethod[level](message)
    if (data && Object.keys(data).length > 0) {
      console.table(logData)
    }
    console.groupEnd()
  } catch (consoleError) {
    // Fallback logging if console.group fails - development only
    console.error('Blog operation logging failed:', consoleError)
    console.log(`[${level.toUpperCase()}] ${message}`, data)
  }
}

/**
 * Generates breadcrumb navigation from a pathname
 * 
 * @param pathname - The current pathname to generate breadcrumbs from
 * @returns Array of breadcrumb objects
 */
export function generateBreadcrumbs(pathname: string): Breadcrumb[] {
  try {
    // Input validation
    if (!pathname || typeof pathname !== 'string') {
      if (IS_DEV) {
        logBlogOperation('warn', 'Invalid pathname provided to generateBreadcrumbs', { pathname })
      }
      return []
    }

    const paths = pathname.split('/').filter(Boolean)
    
    // Early return if no meaningful paths
    if (paths.length <= 1) {
      return []
    }

    const crumbs: Breadcrumb[] = []
    const blogPaths = paths.slice(1) // Remove 'blog' from paths

    blogPaths.forEach((path: string, index: number) => {
      const href = `/blog/${blogPaths.slice(0, index + 1).join('/')}`
      const label = formatForDisplay(path)
      
      // Determine breadcrumb type based on position
      let type: Breadcrumb['type'] = 'article'
      if (index === 0) type = 'category'
      else if (index === 1) type = 'subcategory'
      
      crumbs.push({
        label,
        href,
        current: index === blogPaths.length - 1,
        type
      })
    })

    return crumbs
  } catch (error) {
    logBlogError(
      error instanceof Error ? error : new Error('Unknown breadcrumb generation error'),
      'Breadcrumb generation',
      pathname,
      undefined,
      'low',
      { pathname, pathsLength: pathname?.split('/').length }
    )
    return []
  }
}

/**
 * Utility function to safely parse and validate blog-related data
 * 
 * @param data - Raw data to parse
 * @param schema - Optional validation schema function
 * @returns Parsed and validated data or null if invalid
 */
export function safeParseBlogData<T>(
  data: unknown,
  schema?: (data: unknown) => data is T
): T | null {
  try {
    if (schema && !schema(data)) {
      if (IS_DEV) {
        logBlogOperation('warn', 'Blog data failed schema validation', { data })
      }
      return null
    }
    
    return data as T
  } catch (error) {
    logBlogError(
      error instanceof Error ? error : new Error('Blog data parsing failed'),
      'Data parsing',
      undefined,
      undefined,
      'low',
      { data }
    )
    return null
  }
}